import styles from './onboarding.module.scss';
import {useCallback, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {Form} from 'react-router-dom';

import {SlidingWizard, SlidingWizardHandle} from '../../components/sliding_form_wizard/sliding_wizard';
import {PersonalDetailsContent, PersonalDetailsFooter, PersonalDetailsTitle} from './components/personal_details';
import {LocationDetailsContent, LocationDetailsFooter, LocationDetailsTitle} from './components/location_details';
import {ContactDetailsContent, ContactDetailsFooter, ContactDetailsTitle} from './components/contact_details';
import {VehicleDetailsContent, VehicleDetailsFooter, VehicleDetailsTitle} from './components/vehicle_details';
import {PrivacyAgreementContent, PrivacyAgreementFooter, PrivacyAgreementTitle} from './components/privacy_agreement';
import {PrivacyModal} from './components/privacy_modal';
import {CreateVehicleModal} from '../../components/vehicle/create_vehicle_modal.tsx';
import {useAppSelector} from '../../store/store.ts';
import {DeleteVehicleModal} from '../../components/vehicle/delete_vehicle_modal.tsx';
import {Vehicle} from '../../gql/customer/graphql.ts';

export function Onboarding() {
    const onboardingSliderRef = useRef<SlidingWizardHandle>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [canSubmit, setCanSubmit] = useState(true);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
    const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
    const [isDeleteVehicleModalOpen, setIsDeleteVehicleModalOpen] = useState(false);
    const [toDeleteVehicle, setToDeleteVehicle] = useState<Vehicle | null>(null);

    // Temporary get vehicles of customer to show a list in the modal
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const vehicles = customerInfo.me?.customer?.vehicles;
    // End Temporary

    const onPreviousSlide = useCallback(() => {
        if (isEditing) {
            flushSync(() => setIsEditing(false));
        }
        onboardingSliderRef.current?.slidePrev();
    }, [onboardingSliderRef, isEditing, setIsEditing]);
    const onNextSlide = useCallback(() => {
        if (isEditing) {
            flushSync(() => setIsEditing(false));
        }
        onboardingSliderRef.current?.slideNext();
    }, [onboardingSliderRef, isEditing, setIsEditing]);

    const onDeleteVehicleClick = (vehicle: Vehicle | null) => {
        if (vehicle) {
            setToDeleteVehicle(vehicle);
            setIsDeleteVehicleModalOpen(true);
        }
    };

    return (
        <div className={styles.onboarding}>
            {isPrivacyModalOpen && <PrivacyModal onCloseModal={() => setIsPrivacyModalOpen(false)} />}
            {isCreateVehicleModalOpen && (
                <CreateVehicleModal onCloseModal={() => setIsCreateVehicleModalOpen(false)} vehicles={vehicles} />
            )}
            {isDeleteVehicleModalOpen && toDeleteVehicle && (
                <DeleteVehicleModal
                    onCloseModal={() => {
                        setIsDeleteVehicleModalOpen(false);
                        setToDeleteVehicle(null);
                    }}
                    vehicle={toDeleteVehicle}
                />
            )}

            <Form method="PUT" id="onboarding_form" className={styles.onboarding_content}>
                <SlidingWizard
                    isEditing={isEditing}
                    ref={onboardingSliderRef}
                    slides={[
                        {
                            onPreviousClick: null,
                            title: <PersonalDetailsTitle />,
                            content: (
                                <PersonalDetailsContent
                                    onEditClick={() => setIsEditing(true)}
                                    isEditing={isEditing}
                                    setCanSubmit={setCanSubmit}
                                />
                            ),
                            footer: (
                                <PersonalDetailsFooter
                                    isEditing={isEditing}
                                    setIsEditing={(isEditing) => setIsEditing(isEditing)}
                                    onNextSlide={onNextSlide}
                                    canSubmit={canSubmit}
                                />
                            ),
                        },
                        {
                            onPreviousClick: onPreviousSlide,
                            title: <LocationDetailsTitle />,
                            content: (
                                <LocationDetailsContent onEditClick={() => setIsEditing(true)} isEditing={isEditing} />
                            ),
                            footer: (
                                <LocationDetailsFooter
                                    isEditing={isEditing}
                                    setIsEditing={(isEditing) => setIsEditing(isEditing)}
                                    onPrevSlide={onPreviousSlide}
                                    onNextSlide={onNextSlide}
                                />
                            ),
                        },
                        {
                            onPreviousClick: onPreviousSlide,
                            title: <ContactDetailsTitle />,
                            content: (
                                <ContactDetailsContent
                                    onEditClick={() => setIsEditing(true)}
                                    isEditing={isEditing}
                                    setCanSubmit={setCanSubmit}
                                />
                            ),
                            footer: (
                                <ContactDetailsFooter
                                    isEditing={isEditing}
                                    setIsEditing={(isEditing) => setIsEditing(isEditing)}
                                    onPrevSlide={onPreviousSlide}
                                    onNextSlide={onNextSlide}
                                    canSubmit={canSubmit}
                                />
                            ),
                        },
                        {
                            onPreviousClick: onPreviousSlide,
                            title: <VehicleDetailsTitle />,
                            content: (
                                <VehicleDetailsContent
                                    onDeleteClick={(vehicle) => onDeleteVehicleClick(vehicle)}
                                    onEditClick={() => setIsEditing(true)}
                                    isEditing={isEditing}
                                />
                            ),
                            footer: (
                                <VehicleDetailsFooter
                                    onCreateVehicle={() => setIsCreateVehicleModalOpen(true)}
                                    onPrevSlide={onPreviousSlide}
                                    onNextSlide={onNextSlide}
                                />
                            ),
                        },
                        {
                            onPreviousClick: onPreviousSlide,
                            title: <PrivacyAgreementTitle />,
                            content: <PrivacyAgreementContent />,
                            footer: <PrivacyAgreementFooter onDisagree={() => setIsPrivacyModalOpen(true)} />,
                        },
                    ]}
                />
            </Form>
        </div>
    );
}
